// Canonical list of host-event types. Both the create-form picker and the
// listing-page filter render from this list, and the server validates that
// stored slugs come from this set. Adding a new type = appending one entry;
// no schema migration needed (event_details.types is a free-form jsonb
// string[] gated only by this list at the application layer).

export const EVENT_TYPE_GROUPS = [
  { slug: "food",      label: "Food & Drink" },
  { slug: "nightlife", label: "Nightlife" },
  { slug: "arts",      label: "Arts & Culture" },
  { slug: "games",     label: "Games" },
  { slug: "outdoors",  label: "Sports & Outdoors" },
  { slug: "wellness",  label: "Wellness" },
  { slug: "social",    label: "Learning & Social" },
  { slug: "community", label: "Community" },
  { slug: "attire",    label: "Attire" },
  { slug: "venue",     label: "Venue" },
] as const;

export type EventTypeGroupSlug = (typeof EVENT_TYPE_GROUPS)[number]["slug"];

export const EVENT_TYPES = [
  // Food & Drink
  { slug: "coffee",          emoji: "☕",  label: "Coffee",          group: "food" },
  { slug: "dinner",          emoji: "🍽️", label: "Dinner",          group: "food",      related: ["brunch", "drinks", "potluck"] },
  { slug: "brunch",          emoji: "🍳", label: "Brunch",          group: "food",      related: ["dinner", "drinks", "picnic"] },
  { slug: "drinks",          emoji: "🍸", label: "Drinks",          group: "food",      related: ["happy-hour", "pub-crawl", "cocktail-making", "wine-tasting"] },
  { slug: "happy-hour",      emoji: "🍻", label: "Happy hour",      group: "food",      related: ["drinks", "pub-crawl"] },
  { slug: "wine-tasting",    emoji: "🍷", label: "Wine tasting",    group: "food",      related: ["drinks", "cocktail-making"] },
  { slug: "cocktail-making", emoji: "🍹", label: "Cocktail making", group: "food",      related: ["drinks", "wine-tasting"] },
  { slug: "cooking",         emoji: "👨‍🍳", label: "Cooking",         group: "food",      related: ["baking", "potluck"] },
  { slug: "baking",          emoji: "🧁", label: "Baking",          group: "food",      related: ["cooking", "potluck"] },
  { slug: "potluck",         emoji: "🥘", label: "Potluck",         group: "food",      related: ["dinner", "picnic", "cooking"] },
  { slug: "picnic",          emoji: "🧺", label: "Picnic",          group: "food",      related: ["outdoor", "beach", "potluck"] },
  // Nightlife
  { slug: "club-night",      emoji: "🪩", label: "Club night",      group: "nightlife", related: ["dance", "rave", "party"] },
  { slug: "dance",           emoji: "💃", label: "Dance",           group: "nightlife", related: ["club-night", "rave", "music"] },
  { slug: "concert",         emoji: "🎸", label: "Concert",         group: "nightlife", related: ["music", "festival", "open-mic"] },
  { slug: "karaoke",         emoji: "🎤", label: "Karaoke",         group: "nightlife", related: ["open-mic", "music"] },
  { slug: "drag",            emoji: "👑", label: "Drag show",       group: "nightlife", related: ["cabaret", "club-night", "pride"] },
  { slug: "cabaret",         emoji: "✨", label: "Cabaret",         group: "nightlife", related: ["drag", "theater", "open-mic"] },
  { slug: "open-mic",        emoji: "🎙️", label: "Open mic",        group: "nightlife", related: ["karaoke", "music", "comedy", "poetry"] },
  { slug: "music",           emoji: "🎵", label: "Music",           group: "nightlife", related: ["concert", "dance", "club-night"] },
  { slug: "pub-crawl",       emoji: "🍺", label: "Pub crawl",       group: "nightlife", related: ["drinks", "happy-hour", "party"] },
  { slug: "party",           emoji: "🎉", label: "Party",           group: "nightlife", related: ["pool-party", "foam-party", "rave", "club-night"] },
  { slug: "pool-party",      emoji: "🏊", label: "Pool party",      group: "nightlife", related: ["party", "foam-party", "swimwear", "beach"] },
  { slug: "foam-party",      emoji: "🫧", label: "Foam party",      group: "nightlife", related: ["pool-party", "party", "club-night"] },
  { slug: "rave",            emoji: "🔦", label: "Rave",            group: "nightlife", related: ["club-night", "dance", "party", "festival"] },
  { slug: "rooftop",         emoji: "🌆", label: "Rooftop",         group: "nightlife", related: ["party", "drinks", "outdoor"] },
  { slug: "festival",        emoji: "🎪", label: "Festival",        group: "nightlife", related: ["concert", "rave", "outdoor", "music"] },
  // Arts & Culture
  { slug: "improv",          emoji: "🃏", label: "Improv",          group: "arts",      related: ["comedy", "theater"] },
  { slug: "theater",         emoji: "🎭", label: "Theater",         group: "arts",      related: ["improv", "cabaret", "movie"] },
  { slug: "comedy",          emoji: "😄", label: "Comedy",          group: "arts",      related: ["improv", "open-mic", "theater"] },
  { slug: "movie",           emoji: "🎬", label: "Movie",           group: "arts",      related: ["film-club", "theater"] },
  { slug: "film-club",       emoji: "🎞️", label: "Film club",       group: "arts",      related: ["movie", "book-club", "discussion"] },
  { slug: "art",             emoji: "🎨", label: "Art",             group: "arts",      related: ["crafts", "pottery", "photography", "museum"] },
  { slug: "crafts",          emoji: "🧶", label: "Crafts",          group: "arts",      related: ["art", "pottery", "workshop"] },
  { slug: "pottery",         emoji: "🏺", label: "Pottery",         group: "arts",      related: ["art", "crafts", "workshop"] },
  { slug: "poetry",          emoji: "📝", label: "Poetry",          group: "arts",      related: ["open-mic", "book-club", "discussion"] },
  { slug: "museum",          emoji: "🏛️", label: "Museum",          group: "arts",      related: ["art", "photography"] },
  { slug: "photography",     emoji: "📸", label: "Photography",     group: "arts",      related: ["art", "outdoor", "museum"] },
  // Games
  { slug: "trivia",          emoji: "🎲", label: "Trivia",          group: "games",     related: ["games", "board-games", "discussion"] },
  { slug: "games",           emoji: "🎮", label: "Games",           group: "games",     related: ["board-games", "video-games", "card-games", "trivia"] },
  { slug: "board-games",     emoji: "🎯", label: "Board games",     group: "games",     related: ["games", "card-games", "tabletop-rpg", "trivia"] },
  { slug: "card-games",      emoji: "♟️", label: "Card games",      group: "games",     related: ["board-games", "games", "trivia"] },
  { slug: "video-games",     emoji: "🕹️", label: "Video games",     group: "games",     related: ["games", "hackathon"] },
  { slug: "tabletop-rpg",    emoji: "🐉", label: "Tabletop RPG",    group: "games",     related: ["board-games", "games", "card-games"] },
  { slug: "escape-room",     emoji: "🔐", label: "Escape room",     group: "games",     related: ["games", "trivia"] },
  { slug: "bowling",         emoji: "🎳", label: "Bowling",         group: "games",     related: ["games", "sports"] },
  // Sports & Outdoors
  { slug: "hike",            emoji: "🥾", label: "Hike",            group: "outdoors",  related: ["outdoor", "camping", "climbing", "running"] },
  { slug: "outdoor",         emoji: "🌳", label: "Outdoors",        group: "outdoors",  related: ["hike", "camping", "picnic", "beach"] },
  { slug: "beach",           emoji: "🏖️", label: "Beach",           group: "outdoors",  related: ["swimming", "outdoor", "picnic", "pool-party"] },
  { slug: "camping",         emoji: "⛺", label: "Camping",         group: "outdoors",  related: ["hike", "outdoor"] },
  { slug: "cycling",         emoji: "🚴", label: "Cycling",         group: "outdoors",  related: ["running", "outdoor", "sports"] },
  { slug: "running",         emoji: "🏃", label: "Running",         group: "outdoors",  related: ["cycling", "fitness", "sports", "hike"] },
  { slug: "climbing",        emoji: "🧗", label: "Climbing",        group: "outdoors",  related: ["hike", "outdoor", "sports"] },
  { slug: "swimming",        emoji: "🏊", label: "Swimming",        group: "outdoors",  related: ["beach", "pool-party", "sports"] },
  { slug: "skating",         emoji: "⛸️", label: "Skating",         group: "outdoors",  related: ["sports", "outdoor"] },
  { slug: "sports",          emoji: "⚽", label: "Sports",          group: "outdoors",  related: ["fitness", "running", "cycling", "swimming"] },
  // Wellness
  { slug: "yoga",            emoji: "🧘‍♀️", label: "Yoga",            group: "wellness",  related: ["meditation", "wellness", "fitness"] },
  { slug: "fitness",         emoji: "🧘", label: "Fitness",         group: "wellness",  related: ["yoga", "sports", "running", "wellness"] },
  { slug: "wellness",        emoji: "💚", label: "Wellness",        group: "wellness",  related: ["yoga", "meditation", "spa", "fitness"] },
  { slug: "spa",             emoji: "🧖", label: "Spa & sauna",     group: "wellness",  related: ["wellness", "meditation"] },
  { slug: "meditation",      emoji: "🪷", label: "Meditation",      group: "wellness",  related: ["yoga", "wellness", "spa"] },
  // Learning & Social
  { slug: "workshop",          emoji: "✍️", label: "Workshop",          group: "social",   related: ["hackathon", "discussion", "crafts", "pottery"] },
  { slug: "hackathon",         emoji: "💻", label: "Hackathon",         group: "social",   related: ["workshop", "video-games", "networking"] },
  { slug: "language-exchange", emoji: "🌍", label: "Language exchange", group: "social",   related: ["discussion", "mixer", "networking"] },
  { slug: "discussion",        emoji: "💬", label: "Discussion",        group: "social",   related: ["book-club", "workshop", "support-group"] },
  { slug: "networking",        emoji: "💼", label: "Networking",        group: "social",   related: ["mixer", "speed-friending", "hackathon"] },
  { slug: "mixer",             emoji: "🥂", label: "Mixer",             group: "social",   related: ["networking", "speed-friending", "drinks"] },
  { slug: "book-club",         emoji: "📚", label: "Book club",         group: "social",   related: ["discussion", "film-club", "poetry"] },
  { slug: "speed-dating",      emoji: "💘", label: "Speed dating",      group: "social",   related: ["mixer", "speed-friending"] },
  { slug: "speed-friending",   emoji: "💛", label: "Speed friending",   group: "social",   related: ["mixer", "networking", "speed-dating"] },
  { slug: "support-group", emoji: "🫂", label: "Support group",   group: "social", related: ["discussion", "sober", "activism"] },
  { slug: "activism",      emoji: "✊", label: "Activism",        group: "social", related: ["fundraiser", "volunteering", "support-group"] },
  { slug: "fundraiser",    emoji: "🫶", label: "Fundraiser",      group: "social", related: ["activism", "volunteering"] },
  { slug: "volunteering",  emoji: "🤝", label: "Volunteering",    group: "social", related: ["fundraiser", "activism"] },
  // Community
  { slug: "sapphic",       emoji: "🌸", label: "Sapphic",         group: "community",  related: ["queer", "bi", "pride"] },
  { slug: "gay",           emoji: "💙", label: "Gay",             group: "community",  related: ["queer", "bear", "leather", "pride"] },
  { slug: "queer",         emoji: "⚧️",  label: "Queer",           group: "community",  related: ["gay", "trans", "non-binary", "bi", "sapphic", "pride"] },
  { slug: "trans",         emoji: "🏳️‍⚧️", label: "Trans",           group: "community",  related: ["queer", "non-binary", "pride"] },
  { slug: "non-binary",    emoji: "🌻", label: "Non-binary",      group: "community",  related: ["trans", "queer"] },
  { slug: "bi",            emoji: "💜", label: "Bi",              group: "community",  related: ["queer", "sapphic", "pride"] },
  { slug: "pride",         emoji: "🌈", label: "Pride",           group: "community", related: ["drag", "queer", "festival"] },
  { slug: "bipoc",         emoji: "🫶🏽", label: "BIPOC",           group: "community", related: ["queer", "pride", "activism"] },  

  { slug: "leather",       emoji: "🖤", label: "Leather",         group: "community",  related: ["bear", "kink", "leather-attire"] },
  { slug: "bear",          emoji: "🐻", label: "Bear",            group: "community",  related: ["leather", "kink", "gay"] },
  { slug: "kink",          emoji: "🔗", label: "Kink",            group: "community",  related: ["leather", "bear", "clothing-optional"] },
  { slug: "sober",         emoji: "🫖", label: "Sober",           group: "community", related: ["support-group", "wellness", "coffee"] },
  { slug: "age-18-plus",   emoji: "1️⃣",  label: "18+",             group: "community" },
  { slug: "age-21-plus",   emoji: "2️⃣",  label: "21+",             group: "community" },
  { slug: "age-30-plus",   emoji: "3️⃣",  label: "30+",             group: "community",  related: ["age-40-plus"] },
  { slug: "age-40-plus",   emoji: "4️⃣",  label: "40+",             group: "community",  related: ["age-30-plus", "age-50-plus"] },
  { slug: "age-50-plus",   emoji: "5️⃣",  label: "50+",             group: "community",  related: ["age-40-plus", "age-60-plus"] },
  { slug: "age-60-plus",   emoji: "6️⃣",  label: "60+",             group: "community",  related: ["age-50-plus"] },
  // Attire
  { slug: "black-tie",         emoji: "🤵", label: "Black tie",        group: "attire",    related: ["cocktail-attire", "smart-casual"] },
  { slug: "cocktail-attire",   emoji: "👗", label: "Cocktail attire",  group: "attire",    related: ["black-tie", "smart-casual"] },
  { slug: "smart-casual",      emoji: "👔", label: "Smart casual",     group: "attire",    related: ["cocktail-attire", "casual"] },
  { slug: "casual",            emoji: "👕", label: "Casual",           group: "attire",    related: ["smart-casual", "sportswear"] },
  { slug: "costume",           emoji: "🎭", label: "Costume",          group: "attire",    related: ["themed", "drag"] },
  { slug: "themed",            emoji: "🪄", label: "Themed",           group: "attire",    related: ["costume", "white-party"] },
  { slug: "white-party",       emoji: "🤍", label: "White party",      group: "attire",    related: ["themed", "party"] },
  { slug: "underwear",         emoji: "🩲", label: "Underwear",        group: "attire",    related: ["clothing-optional", "kink"] },
  { slug: "swimwear",          emoji: "🩱", label: "Swimwear",         group: "attire",    related: ["pool-party", "beach", "clothing-optional"] },
  { slug: "sportswear",        emoji: "🏋️", label: "Sportswear",       group: "attire",    related: ["casual", "fitness", "sports"] },
  { slug: "leather-attire",    emoji: "🖤", label: "Leather/gear",     group: "attire",    related: ["leather", "kink"] },
  { slug: "clothing-optional", emoji: "👕", label: "Clothing optional", group: "attire",   related: ["underwear", "kink", "swimwear"] },
  // Venue
  { slug: "bar",          emoji: "🥃", label: "Bar",          group: "venue",  related: ["club-night", "drinks", "pub-crawl"] },
  { slug: "nightclub",    emoji: "🌃", label: "Nightclub",    group: "venue",  related: ["club-night", "dance", "rave"] },
  { slug: "restaurant",   emoji: "🍽️", label: "Restaurant",   group: "venue",  related: ["dinner", "brunch"] },
  { slug: "concert-hall", emoji: "🎼", label: "Concert hall", group: "venue",  related: ["concert", "music"] },
  { slug: "park",         emoji: "🌿", label: "Park",         group: "venue",  related: ["outdoor", "picnic", "running"] },
  { slug: "gym",          emoji: "🏟️", label: "Gym / Studio", group: "venue",  related: ["fitness", "yoga", "sports"] },
  { slug: "pool",         emoji: "💦", label: "Pool",         group: "venue",  related: ["swimming", "pool-party", "swimwear"] },
  { slug: "art-gallery",  emoji: "🖼️", label: "Art gallery",  group: "venue",  related: ["art", "museum", "photography"] },
  { slug: "private-home", emoji: "🏠", label: "Private home", group: "venue",  related: ["party", "dinner"] },
  { slug: "hotel",        emoji: "🏨", label: "Hotel",        group: "venue" },
  { slug: "boat",         emoji: "⛵", label: "Boat / Yacht", group: "venue",  related: ["party", "drinks", "outdoor"] },
] as const;

export type EventType = (typeof EVENT_TYPES)[number];
export type EventTypeSlug = EventType["slug"];

export const MAX_EVENT_TYPES_PER_EVENT = 5;

const SLUG_SET = new Set<string>(EVENT_TYPES.map((t) => t.slug));
const BY_SLUG: Record<string, EventType> = Object.fromEntries(
  EVENT_TYPES.map((t) => [t.slug, t]),
);

export function isEventTypeSlug(value: unknown): value is EventTypeSlug {
  return typeof value === "string" && SLUG_SET.has(value);
}

export function getEventType(slug: string): EventType | null {
  return BY_SLUG[slug] ?? null;
}

// ---------------------------------------------------------------------------
// Themed carousel groups — curated buckets of related tags.
// The server picks the top-N by event count and builds one carousel per group.

export type CarouselTheme = {
  id: string;
  emoji: string;
  title: string;
  tags: readonly string[];
};

// Broad, fuller buckets sized to live inventory (so rows don't fragment into
// 1-3-event slivers) — ORDER matters: themeForTypes / the feed bucket an event
// into the FIRST theme it matches, so the most-defining scene leads (e.g. drag
// before generic nightlife, so a drag-night-at-a-bar reads as drag, not party).
// Identity (queer/gay/pride/sapphic/trans/bi/bipoc) is a cross-cutting modifier,
// NOT a bucket primary — those events land in their activity row and the rest
// fall to the "More to explore" catch-all. Age + pure-structural attire likewise
// fall through. Activity / venue / scene-attire slugs each live in exactly one bucket.
export const CAROUSEL_THEMES: CarouselTheme[] = [
  { id: "main-character",   emoji: "🎤", title: "Shows",        tags: ["drag","cabaret","karaoke","open-mic","theater","comedy","improv","poetry","concert","festival","concert-hall","costume"] },
  { id: "last-call",        emoji: "🪩", title: "After Dark",   tags: ["club-night","dance","nightclub","rave","foam-party","pool-party","party","music","rooftop","boat","white-party","underwear","themed","pub-crawl"] },
  { id: "pour-decisions",   emoji: "🍸", title: "Drinks",       tags: ["drinks","happy-hour","coffee","brunch","dinner","wine-tasting","cocktail-making","cooking","baking","potluck","picnic","restaurant","bar","private-home","hotel"] },
  { id: "soft-launch",      emoji: "🫶", title: "Your People",  tags: ["mixer","networking","speed-dating","speed-friending","discussion","book-club","workshop","hackathon","language-exchange","support-group","activism","volunteering","fundraiser","sober"] },
  { id: "chaotic-good",     emoji: "🎲", title: "Game Night",   tags: ["games","trivia","board-games","card-games","video-games","tabletop-rpg","escape-room","bowling"] },
  { id: "touch-grass",      emoji: "🌳", title: "Outside",      tags: ["hike","outdoor","beach","camping","cycling","running","climbing","swimming","skating","sports","park","yoga","fitness","wellness","meditation","spa","gym","pool","swimwear","sportswear"] },
  { id: "culture-vultures", emoji: "🎭", title: "Galleries",    tags: ["art","crafts","pottery","photography","museum","art-gallery","movie","film-club"] },
  { id: "leather-season",   emoji: "🖤", title: "Leather",      tags: ["leather","bear","kink","leather-attire","clothing-optional"] },
];

// The curated carousel theme an event belongs to — the first theme (in
// CAROUSEL_THEMES order) whose tag-set the event's types intersect, else null.
// Single source of truth for "which curated row does this event live in", so the
// filtered events view can show the same branded titles as the home feed instead
// of generic group names. Returns null for events that match no theme (callers
// bucket those into a catch-all row so nothing is hidden).
export function themeForTypes(types: readonly string[]): CarouselTheme | null {
  for (const theme of CAROUSEL_THEMES) {
    if (types.some((t) => (theme.tags as readonly string[]).includes(t))) {
      return theme;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------

/** Natural plural title for a single-tag carousel, e.g. "Club Nights", "Drag Shows". */
const CAROUSEL_LABELS: Partial<Record<string, string>> = {
  // Nightlife
  "club-night":      "Club Nights",
  "dance":           "Dance Nights",
  "concert":         "Concerts & Live Music",
  "karaoke":         "Karaoke Nights",
  "drag":            "Drag Shows",
  "cabaret":         "Cabaret Nights",
  "open-mic":        "Open Mic Nights",
  "music":           "Music Events",
  "pub-crawl":       "Pub Crawls",
  "party":           "Parties",
  "pool-party":      "Pool Parties",
  "foam-party":      "Foam Parties",
  "rave":            "Raves",
  "rooftop":         "Rooftop Nights",
  "festival":        "Festivals",
  // Food & Drink
  "coffee":          "Coffee Meetups",
  "dinner":          "Dinners",
  "brunch":          "Brunches",
  "drinks":          "Drinks Nights",
  "happy-hour":      "Happy Hours",
  "wine-tasting":    "Wine Tastings",
  "cocktail-making": "Cocktail Classes",
  "cooking":         "Cooking Classes",
  "baking":          "Baking Classes",
  "potluck":         "Potlucks",
  "picnic":          "Picnics",
  // Arts & Culture
  "theater":         "Theater Nights",
  "comedy":          "Comedy Nights",
  "improv":          "Improv Nights",
  "movie":           "Movie Nights",
  "film-club":       "Film Clubs",
  "art":             "Art Shows",
  "crafts":          "Crafts Nights",
  "pottery":         "Pottery Classes",
  "poetry":          "Poetry Nights",
  "museum":          "Museum Visits",
  "photography":     "Photo Walks",
  // Games
  "trivia":          "Trivia Nights",
  "games":           "Game Nights",
  "board-games":     "Board Game Nights",
  "card-games":      "Card Game Nights",
  "video-games":     "Gaming Nights",
  "tabletop-rpg":    "Tabletop RPG",
  "escape-room":     "Escape Rooms",
  "bowling":         "Bowling Nights",
  // Sports & Outdoors
  "hike":            "Hikes",
  "outdoor":         "Outdoor Adventures",
  "beach":           "Beach Days",
  "camping":         "Camping Trips",
  "cycling":         "Bike Rides",
  "running":         "Group Runs",
  "climbing":        "Climbing",
  "swimming":        "Swims",
  "skating":         "Skate Nights",
  "sports":          "Sports Meetups",
  // Wellness
  "yoga":            "Yoga Classes",
  "fitness":         "Fitness Classes",
  "wellness":        "Wellness Events",
  "spa":             "Spa Days",
  "meditation":      "Meditation",
  // Learning & Social
  "workshop":           "Workshops",
  "hackathon":          "Hackathons",
  "language-exchange":  "Language Exchanges",
  "discussion":         "Discussions",
  "networking":         "Networking Events",
  "mixer":              "Mixers",
  "book-club":          "Book Clubs",
  "speed-dating":       "Speed Dating",
  "speed-friending":    "Speed Friending",
  "support-group":      "Support Groups",
  "activism":           "Activism Events",
  "fundraiser":         "Fundraisers",
  "volunteering":       "Volunteering Events",
  // Community / Identity
  "sapphic":         "Sapphic Events",
  "gay":             "Gay Events",
  "trans":           "Trans Events",
  "non-binary":      "Non-Binary Events",
  "bi":              "Bi Events",
  "pride":           "Pride Events",
  "bipoc":           "BIPOC Events",
  "leather":         "Leather Events",
  "bear":            "Bear Events",
  "kink":            "Kink Events",
  "sober":           "Sober Events",
  // Attire
  "black-tie":          "Black Tie Events",
  "cocktail-attire":    "Cocktail Attire Events",
  "costume":            "Costume Events",
  "themed":             "Themed Events",
  "white-party":        "White Parties",
  "underwear":          "Underwear Events",
  "swimwear":           "Swimwear Events",
  "leather-attire":     "Leather & Gear Nights",
  // Venue
  "bar":             "Bar Nights",
  "nightclub":       "Club Nights",
  "restaurant":      "Restaurant Dinners",
  "boat":            "Boat Parties",
  "park":            "Park Events",
  "pool":            "Pool Events",
  "art-gallery":     "Gallery Nights",
  "concert-hall":    "Concert Nights",
  "gym":             "Gym Events",
  "private-home":    "Home Gatherings",
  "hotel":           "Hotel Events",
};

export function carouselLabel(slug: string): string {
  return CAROUSEL_LABELS[slug] ?? getEventType(slug)?.label ?? slug;
}

// ---------------------------------------------------------------------------
// Per-event poster label — reads the full types array and produces a specific
// heading like "SAPPHIC DRAG NIGHT" instead of "COMMUNITY EVENT".

const IDENTITY_PREFIX = [
  { slug: "sapphic",    label: "SAPPHIC"    },
  { slug: "bear",       label: "BEAR"       },
  { slug: "leather",    label: "LEATHER"    },
  { slug: "kink",       label: "KINK"       },
  { slug: "trans",      label: "TRANS"      },
  { slug: "non-binary", label: "NON-BINARY" },
  { slug: "bi",         label: "BI"         },
  { slug: "bipoc",      label: "BIPOC"      },
  { slug: "gay",        label: "GAY"        },
  { slug: "pride",      label: "PRIDE"      },
  { slug: "sober",      label: "SOBER"      },
];

type ComboRule = { requires: string[]; any?: string[]; label: string };
const EVENT_LABEL_RULES: ComboRule[] = [
  { requires: ["drag", "cabaret"],                                           label: "DRAG CABARET"      },
  { requires: ["pride", "festival"],                                         label: "PRIDE FESTIVAL"    },
  { requires: ["drag"],   any: ["club-night","nightclub","bar","party"],     label: "DRAG NIGHT"        },
  { requires: ["dance"],  any: ["club-night","nightclub","rave"],            label: "DANCE NIGHT"       },
  { requires: ["leather-attire"], any: ["leather","kink","bear"],            label: "LEATHER NIGHT"     },
  { requires: ["movie", "film-club"],                                        label: "FILM CLUB"         },
  { requires: ["foam-party"],        label: "FOAM PARTY"        },
  { requires: ["pool-party"],        label: "POOL PARTY"        },
  { requires: ["pub-crawl"],         label: "PUB CRAWL"         },
  { requires: ["speed-dating"],      label: "SPEED DATING"      },
  { requires: ["speed-friending"],   label: "SPEED FRIENDING"   },
  { requires: ["escape-room"],       label: "ESCAPE ROOM"       },
  { requires: ["tabletop-rpg"],      label: "TABLETOP RPG"      },
  { requires: ["cocktail-making"],   label: "COCKTAIL CLASS"    },
  { requires: ["wine-tasting"],      label: "WINE TASTING"      },
  { requires: ["language-exchange"], label: "LANGUAGE EXCHANGE" },
  { requires: ["support-group"],     label: "SUPPORT GROUP"     },
  { requires: ["book-club"],         label: "BOOK CLUB"         },
  { requires: ["film-club"],         label: "FILM CLUB"         },
  { requires: ["open-mic"],          label: "OPEN MIC"          },
  { requires: ["white-party"],       label: "WHITE PARTY"       },
  { requires: ["drag"],              label: "DRAG SHOW"         },
  { requires: ["cabaret"],           label: "CABARET NIGHT"     },
  { requires: ["karaoke"],           label: "KARAOKE NIGHT"     },
  { requires: ["rooftop"],           label: "ROOFTOP NIGHT"     },
  { requires: ["rave"],              label: "RAVE"              },
  { requires: ["festival"],          label: "FESTIVAL"          },
  { requires: ["concert"],           label: "LIVE MUSIC"        },
  { requires: ["club-night"],        label: "CLUB NIGHT"        },
  { requires: ["comedy"],            label: "COMEDY NIGHT"      },
  { requires: ["improv"],            label: "IMPROV NIGHT"      },
  { requires: ["theater"],           label: "THEATER NIGHT"     },
  { requires: ["poetry"],            label: "POETRY NIGHT"      },
  { requires: ["movie"],             label: "MOVIE NIGHT"       },
  { requires: ["art"],               label: "ART SHOW"          },
  { requires: ["crafts"],            label: "CRAFTS NIGHT"      },
  { requires: ["pottery"],           label: "POTTERY CLASS"     },
  { requires: ["photography"],       label: "PHOTO WALK"        },
  { requires: ["museum"],            label: "MUSEUM VISIT"      },
  { requires: ["trivia"],            label: "TRIVIA NIGHT"      },
  { requires: ["board-games"],       label: "GAME NIGHT"        },
  { requires: ["card-games"],        label: "GAME NIGHT"        },
  { requires: ["video-games"],       label: "GAMING NIGHT"      },
  { requires: ["games"],             label: "GAME NIGHT"        },
  { requires: ["bowling"],           label: "BOWLING NIGHT"     },
  { requires: ["yoga"],              label: "YOGA CLASS"        },
  { requires: ["meditation"],        label: "MEDITATION"        },
  { requires: ["fitness"],           label: "FITNESS CLASS"     },
  { requires: ["spa"],               label: "SPA DAY"           },
  { requires: ["hike"],              label: "HIKE"              },
  { requires: ["beach"],             label: "BEACH DAY"         },
  { requires: ["camping"],           label: "CAMPING TRIP"      },
  { requires: ["cycling"],           label: "BIKE RIDE"         },
  { requires: ["running"],           label: "GROUP RUN"         },
  { requires: ["climbing"],          label: "CLIMBING"          },
  { requires: ["swimming"],          label: "SWIM"              },
  { requires: ["skating"],           label: "SKATE NIGHT"       },
  { requires: ["workshop"],          label: "WORKSHOP"          },
  { requires: ["hackathon"],         label: "HACKATHON"         },
  { requires: ["networking"],        label: "NETWORKING"        },
  { requires: ["mixer"],             label: "MIXER"             },
  { requires: ["discussion"],        label: "DISCUSSION"        },
  { requires: ["activism"],          label: "ACTIVISM"          },
  { requires: ["volunteering"],      label: "VOLUNTEERING"      },
  { requires: ["fundraiser"],        label: "FUNDRAISER"        },
  { requires: ["brunch"],            label: "BRUNCH"            },
  { requires: ["dinner"],            label: "DINNER"            },
  { requires: ["happy-hour"],        label: "HAPPY HOUR"        },
  { requires: ["picnic"],            label: "PICNIC"            },
  { requires: ["potluck"],           label: "POTLUCK"           },
  { requires: ["cooking"],           label: "COOKING CLASS"     },
  { requires: ["baking"],            label: "BAKING CLASS"      },
  { requires: ["coffee"],            label: "COFFEE MEETUP"     },
  { requires: ["drinks"],            label: "DRINKS"            },
  { requires: ["party"],             label: "PARTY"             },
  { requires: ["dance"],             label: "DANCE CLASS"       },
  { requires: ["music"],             label: "MUSIC NIGHT"       },
  { requires: ["nightclub"],         label: "CLUB NIGHT"        },
  { requires: ["bar"],               label: "BAR NIGHT"         },
  { requires: ["restaurant"],        label: "DINNER OUT"        },
  { requires: ["concert-hall"],      label: "LIVE MUSIC"        },
  { requires: ["gym"],               label: "WORKOUT"           },
  { requires: ["pool"],              label: "POOL SESSION"      },
  { requires: ["art-gallery"],       label: "GALLERY NIGHT"     },
  { requires: ["park"],              label: "PARK HANGOUT"      },
  { requires: ["boat"],              label: "BOAT PARTY"        },
];

const IDENTITY_FALLBACKS: Partial<Record<string, string>> = {
  sapphic:       "SAPPHIC NIGHT",
  bear:          "BEAR SOCIAL",
  leather:       "LEATHER NIGHT",
  kink:          "KINK NIGHT",
  trans:         "TRANS GATHERING",
  "non-binary":  "NB GATHERING",
  bi:            "BI SOCIAL",
  bipoc:         "BIPOC GATHERING",
  gay:           "GAY SOCIAL",
  pride:         "PRIDE EVENT",
  sober:         "SOBER SOCIAL",
};

export function eventLabel(types: string[]): string {
  const s = new Set(types);
  const identity = IDENTITY_PREFIX.find((ip) => s.has(ip.slug));
  const prefix = identity?.label ?? "QUEER";

  for (const rule of EVENT_LABEL_RULES) {
    const match =
      rule.requires.every((r) => s.has(r)) &&
      (!rule.any || rule.any.some((r) => s.has(r)));
    if (!match) continue;
    return rule.label.includes(prefix) ? rule.label : `${prefix} ${rule.label}`;
  }

  return (identity && IDENTITY_FALLBACKS[identity.slug]) ?? `${prefix} EVENT`;
}

// ---------------------------------------------------------------------------
// Mood presets — server evaluates which have events; client shows chips.

export const MOOD_PRESETS = [
  { id: "sapphic",  emoji: "🌸", label: "Sapphic",  tags: ["sapphic"] },
  { id: "bears",    emoji: "🐻", label: "Bears",    tags: ["bear", "leather", "kink"] },
  { id: "drag",     emoji: "👑", label: "Drag",     tags: ["drag", "cabaret"] },
  { id: "wellness", emoji: "🧘", label: "Wellness", tags: ["yoga", "wellness", "fitness", "meditation", "spa"] },
  { id: "arts",     emoji: "🎭", label: "Arts",     tags: ["theater", "comedy", "improv", "art", "film-club", "poetry", "music", "concert", "cabaret", "open-mic"] },
  { id: "drinks",   emoji: "🍸", label: "Drinks",   tags: ["drinks", "happy-hour", "cocktail-making", "wine-tasting", "pub-crawl", "bar"] },
  { id: "outdoors", emoji: "🥾", label: "Outdoors", tags: ["hike", "outdoor", "beach", "camping", "cycling", "running", "climbing", "swimming", "sports"] },
  { id: "games",    emoji: "🎲", label: "Games",    tags: ["games", "trivia", "board-games", "card-games", "escape-room", "tabletop-rpg", "bowling", "video-games"] },
  { id: "trans",    emoji: "✨", label: "Trans",    tags: ["trans", "non-binary"] },
  { id: "pride",    emoji: "🌈", label: "Pride",    tags: ["pride", "bipoc", "queer"] },
] as const;

export type MoodPresetId = (typeof MOOD_PRESETS)[number]["id"];

// ---------------------------------------------------------------------------

/** Given a search query, returns matching types followed by their related types
 *  (deduped, direct matches listed first). */
export function searchEventTypes(query: string): EventType[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...EVENT_TYPES];

  const direct = EVENT_TYPES.filter((t) => t.label.toLowerCase().includes(q));
  const seen = new Set<string>(direct.map((t) => t.slug));

  const related: EventType[] = [];
  for (const t of direct) {
    for (const slug of (t as { related?: readonly string[] }).related ?? []) {
      if (!seen.has(slug)) {
        const r = BY_SLUG[slug];
        if (r) { related.push(r); seen.add(slug); }
      }
    }
  }

  return [...direct, ...related];
}
